import { EncChannel, EncField, EncFilterField, Encoding } from '@mode-vegas/vegas';
import { BehaviorSubject, Observable, Subject, Subscription, combineLatest } from 'rxjs';
import { first, map, takeUntil } from 'rxjs/operators';

import { SelectedChartService } from '@mode/charts/contract';

import { capraIconChartExplorer } from '@mode/capra';
import { FilterModalService } from '@mode/charts/feature-encoding';
import { EncodingHelpers, SwitchSchemaModel } from '@mode/charts/util-vegas';
import { ChartTypes, FlamingoTypes } from '@mode/shared/contract-common';
import { ConvertToVETooltipMap } from '@mode/shared/util-js';
import { AnalyticsEvent, AnalyticsService } from '@mode/shared/util-segment';
import { CompiledField, Switch } from '@mode/shared/util-switch';
import { ToastService } from '@mode/shared/util-toast';
import { MixableBaseController } from '../../../../common/component';
import { ChartObservables } from '../../../../types/viz-designer.types';
import * as BuilderHelpers from './builder-helpers';

const stackedTypes: ReadonlyArray<ChartTypes.ChartType> = [
  'stackedBar',
  'stackedBar100',
  'line',
  'area',
  'totalArea',
  'scatter',
];
const stackedHTypes: ReadonlyArray<ChartTypes.ChartType> = ['hStackedBar', 'hStackedBar100'];
const groupedTypes: ReadonlyArray<ChartTypes.ChartType> = ['bar'];
const groupedHTypes: ReadonlyArray<ChartTypes.ChartType> = ['hBar'];
const comboTypes: ReadonlyArray<ChartTypes.ChartType> = ['linePlusBar'];
const radialTypes: ReadonlyArray<ChartTypes.ChartType> = ['donut', 'pie'];
const pivotTypes: ReadonlyArray<ChartTypes.ChartType> = ['vegasPivotTable'];

class EncodingBuilderController extends MixableBaseController {
  static $inject = ['$element', 'ToastService', 'AnalyticsService', 'FilterModalService'];

  private observables: ChartObservables;
  private chartService: SelectedChartService;

  private encoding: Encoding;
  private model: FlamingoTypes.ModeModel;

  private queryToken: string;
  private queryRunToken: string;

  private convertToVETooltip = '';

  dragCounter: number;
  dragHovered: EncChannel | 'field-list' | null;

  hoveredChartType = '';
  chartType$: Observable<ChartTypes.ChartType>;
  isStacked$: Observable<boolean>;
  isStackedH$: Observable<boolean>;
  isGrouped$: Observable<boolean>;
  isGroupedH$: Observable<boolean>;
  isCombo$: Observable<boolean>;
  isRadial$: Observable<boolean>;
  isTable$: Observable<boolean>;
  isBigValue$: Observable<boolean>;
  isVegas$: Observable<boolean>;
  isVE$: Observable<boolean>;
  isVegasPivotTable$: Observable<boolean>;
  disableAggregateFilter$: Observable<boolean>;
  isAutoUpdateDisabled$: Observable<boolean>;
  isConvertToVEAllowed$: Observable<boolean>;
  isHistogram$: Observable<boolean>;

  private encodingSubscription: Subscription;
  private updateModeTooltip = '';

  // The following set of properties manage a very trivial bit of functionality.
  // Design has requested that we show a drop-shadow above the 'auto-update'
  // component ONLY when the marks container overflows. This is to give the
  // appearance that the auto-update component is above the marks container.
  // This requires checking the marks container for the presence of a vertical
  // scroll bar and then modifying the class selector on the auto-update
  // component.
  // The resizeObserver/Subject manage resize changes to the marks container
  // which may add/remove the vertical scrollbar.
  private resizeObserver?: ResizeObserver;
  private resizeSubject = new Subject<void>();
  private resizeSubscription!: Subscription | null;
  // The scroll subject is used to update the class selector on the
  // auto-update component.
  private scrollSubject = new BehaviorSubject<boolean>(false);
  private hasScroll$: Observable<boolean>;
  chartType: string;

  constructor(
    private $element: angular.IAugmentedJQuery,
    private alertService: ToastService,
    private analyticsService: AnalyticsService,
    private filterModalService: FilterModalService
  ) {
    super();
  }

  $onInit() {
    this.hasScroll$ = this.scrollSubject.asObservable();
    this.observables.encoding$.pipe(takeUntil(this.destroyed$)).subscribe((encoding) => (this.encoding = encoding));
    this.observables.queryToken$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((queryToken) => (this.queryToken = queryToken));

    this.observables.queryRunToken$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((queryRunToken) => (this.queryRunToken = queryRunToken));

    this.chartType$ = this.observables.chartType$.pipe(takeUntil(this.destroyed$));
    this.chartType$.pipe(takeUntil(this.destroyed$)).subscribe((chartType) => {
      this.chartType = chartType;
      this.convertToVETooltip = this.makeConvertToVETooltip();
    });

    this.isStacked$ = this.chartType$.pipe(map((type) => stackedTypes.indexOf(type) >= 0));
    this.isStackedH$ = this.chartType$.pipe(map((type) => stackedHTypes.indexOf(type) >= 0));
    this.isGrouped$ = this.chartType$.pipe(map((type) => groupedTypes.indexOf(type) >= 0));
    this.isGroupedH$ = this.chartType$.pipe(map((type) => groupedHTypes.indexOf(type) >= 0));
    this.isCombo$ = this.chartType$.pipe(map((type) => comboTypes.indexOf(type) >= 0));
    this.isRadial$ = this.chartType$.pipe(map((type) => radialTypes.indexOf(type) >= 0));
    this.isBigValue$ = this.chartType$.pipe(map((type) => type === 'bigValue'));
    this.isTable$ = this.chartType$.pipe(map((type) => type === 'table'));
    this.isVegas$ = this.chartType$.pipe(
      map((type) => {
        const allowedChartTypes: ChartTypes.ChartType[] = ['vegas', 'vegasPivotTable'];
        return allowedChartTypes.indexOf(type) !== -1;
      })
    );
    this.isVE$ = this.chartType$.pipe(map((type) => type === 'vegas'));
    this.isVegasPivotTable$ = this.chartType$.pipe(map((type) => pivotTypes.indexOf(type) >= 0));

    this.disableAggregateFilter$ = this.isTable$;
    this.isHistogram$ = this.chartType$.pipe(
      map((type) => {
        return type === 'histogram';
      })
    );

    this.setResizeObserver();
    this.resizeSubscription = this.resizeSubject
      .asObservable()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(this.onMarksContainerResize.bind(this));

    this.isAutoUpdateDisabled$ = this.observables.isAutoUpdateDisabled$;
    this.isAutoUpdateDisabled$.pipe(takeUntil(this.destroyed$)).subscribe((isAutoUpdateDisabled) => {
      this.updateModeTooltip = isAutoUpdateDisabled
        ? 'Chart updates only when<br>changes are manually applied'
        : 'Chart updates automatically<br>when changes are made';
    });

    this.isConvertToVEAllowed$ = combineLatest([
      this.chartType$,
      this.observables.encoding$,
      this.observables.model$,
      this.observables.queryToken$,
      this.observables.format$,
    ]).pipe(
      map(([chartType, encoding, model, queryToken, format]) => {
        // No conversion path for vegas->vegas or table/bigValue->vegas.
        // We don't want the convert button to show up for empty encodings.
        if (['vegas', 'table', 'bigValue'].includes(chartType)) {
          return false;
        }

        const schemaModel = SwitchSchemaModel.make(model, queryToken);
        return EncodingHelpers.isSupportedEncodingConversion(chartType, 'vegas', encoding, schemaModel, format);
      })
    );
  }

  $onDestroy() {
    this.removeResizeObserver();
  }

  setResizeObserver() {
    this.removeResizeObserver();
    this.resizeObserver = new ResizeObserver(() => {
      this.resizeSubject.next();
    });
    this.resizeObserver.observe(this.$element[0].children[0]);
  }

  removeResizeObserver() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    this.resizeObserver = undefined;
  }

  onMarksContainerResize(event: any) {
    // Get the marks container element from the injected element
    const childElement = this.$element[0]?.children[0];
    // Check for a vertical scroll
    const hasScroll = childElement && childElement.scrollHeight > childElement.clientHeight;
    // Trigger the scrollSubject with the current scroll state.
    this.scrollSubject.next(hasScroll);
  }

  setIsAutoUpdateDisabled(disable: boolean) {
    this.chartService.updateIsAutoUpdateDisabled(disable);

    combineLatest([this.observables.token$, this.chartType$])
      .pipe(first())
      .subscribe(([token, chartType]) => {
        this.analyticsService.track(AnalyticsEvent.Name.UiClicked, {
          location: 'Encoding Builder',
          action: disable ? 'Enable Manual Mode' : 'Enable Automatic Mode',
          context: 'visualization',
          context_id: token,
          chartType,
        });
      });
  }

  convertToVE() {
    this.chartService.updateChartType('vegas');
  }

  makeConvertToVETooltip() {
    const iconData = capraIconChartExplorer.data;
    const tooltipText = ConvertToVETooltipMap[this.chartType as keyof typeof ConvertToVETooltipMap];
    return `
    <div class='convert-encoding-tooltip'>
      <div class='convert-encoding-description'>
        ${iconData}
        <span>${
          tooltipText ? tooltipText : 'Enhance your charts with facets,<br>dual axes, custom colors, and<br>much more'
        }</span>
      </div>
    </div>`;
  }

  // Filter Field Events
  dragEnter($event: DragEvent, $channel: EncChannel | 'field-list') {
    const dragInfo = this.chartService.dragField;
    if (!dragInfo || dragInfo.source === $channel || !dragInfo.valid) {
      return;
    }

    this.dragCounter = BuilderHelpers.incDragCounter(this.dragHovered, $channel, this.dragCounter);
    this.dragHovered = $channel;
    BuilderHelpers.setDragIcon($event, dragInfo.source, true);
  }

  dragLeave($channel: EncChannel) {
    this.dragCounter = BuilderHelpers.decDragCounter(this.dragHovered, $channel, this.dragCounter);
    if (this.dragCounter <= 0) {
      this.dragHovered = null;
    }
  }

  fieldDropped($channel: EncChannel) {
    this.dragHovered = null;
    const dragInfo = this.chartService.dragField;
    const view = Switch.Views.find(this.model, this.queryToken);

    if (view && dragInfo != null) {
      this.filterModalService.addFilter(
        this.queryRunToken,
        dragInfo.field,
        this.encoding,
        view,
        (filterField: EncFilterField, field: CompiledField) => {
          const newEncoding = EncodingHelpers.addFilterToChartEncoding(this.encoding, filterField, field);
          this.chartService.dispatchEncodingUpdate(newEncoding);
        }
      );
    }

    this.chartService.endDrag();
  }

  dragOver($event: DragEvent) {
    if (this.dragHovered) {
      const dragInfo = this.chartService.dragField;
      BuilderHelpers.setDragIcon($event, dragInfo.source, true);
    } else {
      BuilderHelpers.setDragIcon($event, null, false);
    }
  }

  removeField($field: EncField) {
    const encResult = EncodingHelpers.removeFieldFromEncoding(this.encoding, 'pie', $field.id); // can use any chart type in the shape
    this.chartService.dispatchEncodingUpdate(encResult);
  }

  dragStart($event: DragEvent, $field: CompiledField, $channel: EncChannel, $valid: boolean) {
    this.chartService.startDrag({
      field: $field,
      source: $channel,
      valid: $valid,
    });
  }

  // Note: `parent` should match the `id` attribute of the containing ancestor element within which if
  // the pill gets dropped, it shouldn't be removed (e.g. a pill dragged from the X-Axis shelf dropped
  // within the same shelf, or a pill dragged from the Filters card dropped within the same card, should
  // not be removed)
  dragEnd($event: DragEvent, parent: string, $field?: EncField): void {
    if (
      this.chartService.dragField &&
      !$(document.elementFromPoint($event.clientX, $event.clientY)).closest(`#${parent}`).length
    ) {
      this.removeField($field);
    }
    this.chartService.endDrag();
  }
  // End Filter Field Events
}

export const EncodingBuilderComponent: angular.IComponentOptions = {
  bindings: {
    observables: '<',
    model: '<',
    viewName: '<',
    encoding: '<',
    format: '<',
    chartService: '<',
    isExploring: '<',
    fieldDrop: '&',
    onTriggerEditFilter: '&',
    onUpdate: '&',
    onFieldDrag: '&',
    onFieldRemoved: '&',
    onTypeChanged: '&',
  },
  template: `
<div class="vegas-marks-container" ng-class="{'vegas-encoding-container': (ctrl.isVegas$ | async:this)}">
  <div class="card">
      <div class="card-block" ng-class="{ horizontal: ctrl.isHorizontal, 'no-border': ((ctrl.isVegas$ | async:this) || (ctrl.isTable$ | async:this)) }">
          <table-encoding-builder
            ng-if="ctrl.isTable$ | async:this"
            observables="ctrl.observables"
            encoding="ctrl.encoding"
            format="ctrl.format"
            chart-service="ctrl.chartService"
            model="ctrl.model"
          ></table-encoding-builder>
          <mode-encoding-builder-vegas
            ng-if="ctrl.isVE$ | async:this"
            [observables]="ctrl.observables"
            [encoding]="ctrl.encoding"
            [chart-service]="ctrl.chartService"
            [model]="ctrl.model"
            [is-exploring]="ctrl.isExploring"
            [chart-type]="ctrl.chartType$ | async:this"
          ></mode-encoding-builder-vegas>
          <mode-stacked-builder
            ng-if="(ctrl.isStacked$ | async:this) || (ctrl.isStackedH$ | async:this)"
            [observables]="ctrl.observables"
            [encoding]="ctrl.encoding"
            [chart-service]="ctrl.chartService"
            [model]="ctrl.model"
            [is-exploring]="ctrl.isExploring"
            [chart-type]="ctrl.chartType$ | async:this"
          ></mode-stacked-builder>
          <mode-grouped-builder
            ng-if="(ctrl.isGrouped$ | async:this) || (ctrl.isGroupedH$ | async:this)"
            [observables]="ctrl.observables"
            [encoding]="ctrl.encoding"
            [chart-service]="ctrl.chartService"
            [model]="ctrl.model"
            [is-exploring]="ctrl.isExploring"
            [chart-type]="ctrl.chartType$ | async:this"
          ></mode-grouped-builder>
          <mode-combo-builder
            ng-if="ctrl.isCombo$ | async:this"
            [observables]="ctrl.observables"
            [encoding]="ctrl.encoding"
            [chart-service]="ctrl.chartService"
            [model]="ctrl.model"
            [is-exploring]="ctrl.isExploring"
            [chart-type]="ctrl.chartType$ | async:this"
          ></mode-combo-builder>
          <mode-radial-builder
            ng-if="ctrl.isRadial$ | async:this"
            [observables]="ctrl.observables"
            [encoding]="ctrl.encoding"
            [chart-service]="ctrl.chartService"
            [model]="ctrl.model"
            [is-exploring]="ctrl.isExploring"
            [chart-type]="ctrl.chartType$ | async:this"
          ></mode-radial-builder>
          <mode-pivot-builder
            ng-if="ctrl.isVegasPivotTable$ | async:this"
            [observables]="ctrl.observables"
            [encoding]="ctrl.encoding"
            [chart-service]="ctrl.chartService"
            [model]="ctrl.model"
            [is-exploring]="ctrl.isExploring"
          ></mode-pivot-builder>
          <big-values-encoding-builder
            ng-if="ctrl.isBigValue$ | async:this"
            observables="ctrl.observables"
            encoding="ctrl.encoding"
            chart-service="ctrl.chartService"
            model="ctrl.model"
          ></big-values-encoding-builder>
          <mode-encoding-builder-histogram
            ng-if="ctrl.isHistogram$ | async:this"
            [observables]="ctrl.observables"
            [encoding]="ctrl.encoding"
            [chart-service]="ctrl.chartService"
            [model]="ctrl.model"
            [format]="ctrl.format"
            [is-exploring]="ctrl.isExploring"
            [chart-type]="ctrl.chartType$ | async:this"
          ></mode-encoding-builder-histogram>
      </div>
  </div>
  <div id="marks" class="card" ng-if="ctrl.isVE$ | async:this">
    <mode-marks-panel
      [observables]="ctrl.observables"
      [model]="ctrl.model"
      [chart-service]="ctrl.chartService"
      [is-exploring]="ctrl.isExploring"
    ></mode-marks-panel>
  </div>
  <div id="filter" data-test="filter-zone">
    <div class="filter-card-qc" ng-if="!(ctrl.isVegas$ | async:this)">
      <filter-encoding-builder
        disable-aggregate="ctrl.disableAggregateFilter$ | async:this"
        observables="ctrl.observables"
        encoding="ctrl.encoding"
        chart-service="ctrl.chartService"
        model="ctrl.model"
      ></filter-encoding-builder>
    </div>
    <div class="filter-card-ve" ng-if="ctrl.isVegas$ | async:this">
      <mode-filter-encoding
        [disable-aggregate]="ctrl.disableAggregateFilter$ | async:this"
        [chart-type]="ctrl.chartType$ | async:this"
        [observables]="ctrl.observables"
        [encoding]="ctrl.encoding"
        [alert-service]="ctrl.alertService"
        [chart-service]="ctrl.chartService"
        [filter-modal-service]="ctrl.filterModalService"
        [model]="ctrl.model">
      </mode-filter-encoding>
    </div>
  </div>
  <capra-tooltip
    ng-if="ctrl.isConvertToVEAllowed$ | async:this"
    [tooltip-content]="ctrl.convertToVETooltip"
    [tooltip-delay]="[1000, 0]"
    tooltip-placement="bottom"
    tooltip-content-is-html="true"
    >
    <capra-button
      variant="secondary"
      class="encoding-convert-button"
      text="Convert to Visual Explorer"
      color="neutral"
      size="small"
      aria-label="Convert to Visual Explorer"
      (clicked)="ctrl.convertToVE()">
    </capra-button>
  </capra-tooltip>
</div>
<mode-auto-update-button
  class="auto-update-container"
  ng-class="{ 'drop-shadow': (ctrl.hasScroll$ | async:this) }"
  uib-tooltip-html="ctrl.updateModeTooltip"
  tooltip-class="html-tooltip"
  tooltip-append-to-body="true"
  tooltip-placement="top"
  tooltip-popup-delay="1000"
  [is-auto-update]="!(ctrl.isAutoUpdateDisabled$ | async:this)"
  (update-is-auto-update-disabled)="ctrl.setIsAutoUpdateDisabled($event)">
</mode-auto-update-button>`,
  controller: EncodingBuilderController,
  controllerAs: 'ctrl',
};
